import { Box, Chip, Stack, TextField, Typography } from '@mui/material'
import { useState } from 'react'

import type { Player } from '@/api/types'

export type Side = 'a' | 'b'

//: Chips shown before the panel offers the rest.
//:
//: Thirty people was six rows per panel and twelve rows above the score
//: boxes — the two fields you came to fill, a screen and a half down. Ten is
//: two courts' worth, and the server sends them most-recently-played first,
//: so for a group with regulars these ARE the regulars. Everybody else is one
//: search or one tap away.
const CHIPS_SHOWN = 10

/**
 * Who is on each side, as two panels with a search over each.
 *
 * The list this replaces was one row per player with an A and a B button on
 * it, which reads as a table of people rather than as two teams — you had to
 * assemble each side in your head from a column of letters. Two panels put
 * the thing being built on the screen: Side A is the names under Side A.
 *
 * Tapping a name in the OTHER panel moves them, rather than refusing until
 * they are taken off the first. "Ravi is on the other team" is one thought
 * and should be one tap; making it two is how a form teaches somebody that it
 * is fighting them.
 *
 * A search over each panel, not one shared. They are used at different
 * moments — you fill one side, then the other — and a shared box means
 * clearing it before starting the second, every time.
 *
 * Shared by the courtside page and the manager's correction dialog, because
 * fixing a game is the same job as recording one a day later. `PLAN.md` has
 * what happened the last time one job had two shapes: the pay page and the
 * join page drifted into disagreeing about what a stranger could see.
 */
export function SidePicker({
  players,
  sides,
  onPut,
}: {
  players: Player[]
  sides: Record<string, Side>
  onPut: (personId: string, side: Side | null) => void
}) {
  return (
    <Stack spacing={1.5}>
      {(['a', 'b'] as const).map((side) => (
        <SidePanel
          key={side}
          side={side}
          players={players}
          sides={sides}
          onPut={onPut}
        />
      ))}
    </Stack>
  )
}

function SidePanel({
  side,
  players,
  sides,
  onPut,
}: {
  side: Side
  players: Player[]
  sides: Record<string, Side>
  onPut: (personId: string, side: Side | null) => void
}) {
  const [search, setSearch] = useState('')
  const [all, setAll] = useState(false)

  const needle = search.trim().toLowerCase()
  const matching = needle
    ? players.filter(
        // Somebody already on THIS side stays visible whatever is typed. A
        // teammate vanishing from the panel reads as having been un-picked.
        (p) => p.name.toLowerCase().includes(needle) || sides[p.person_id] === side,
      )
    : players

  // Cut to the first few, plus anybody already picked — being unable to see
  // your own team because it fell past the cut is worse than any amount of
  // scrolling. Searching shows every match: the box exists to reach the rest.
  const cut = !all && !needle && matching.length > CHIPS_SHOWN
  const shown = cut
    ? matching.filter((p, i) => i < CHIPS_SHOWN || sides[p.person_id] === side)
    : matching
  const hidden = matching.length - shown.length

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ mb: 1 }}
      >
        <Typography sx={{ fontWeight: 660, fontSize: '0.9375rem' }}>
          Side {side.toUpperCase()}
        </Typography>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search"
          size="small"
          inputProps={{ 'aria-label': `Search side ${side.toUpperCase()}` }}
          sx={{ flex: 1, minWidth: 0 }}
        />
      </Stack>

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {shown.map((player) => {
          const on = sides[player.person_id]
          const mine = on === side
          return (
            <Chip
              key={player.person_id}
              label={player.name}
              // Tapping the side they are on takes them off; tapping the
              // other one moves them.
              onClick={() => onPut(player.person_id, mine ? null : side)}
              aria-label={`${player.name} on side ${side.toUpperCase()}`}
              aria-pressed={mine}
              sx={{
                minHeight: 42,
                fontSize: '0.9375rem',
                fontWeight: mine ? 680 : 500,
                px: 0.5,
                bgcolor: mine ? 'primary.main' : 'transparent',
                color: mine ? 'primary.contrastText' : 'text.primary',
                border: '1px solid',
                borderColor: mine ? 'primary.main' : 'divider',
                // Dimmed, not hidden. Seeing that Ravi is on the other side is
                // the thing that tells you why he is not here.
                opacity: on && !mine ? 0.4 : 1,
                '&:hover': {
                  bgcolor: mine ? 'primary.dark' : 'action.hover',
                },
              }}
            />
          )
        })}
        {shown.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary', py: 0.5 }}>
            Nobody by that name.
          </Typography>
        )}
        {hidden > 0 && (
          <Chip
            label={`${hidden} more`}
            variant="outlined"
            onClick={() => setAll(true)}
            aria-label={`Show all for side ${side.toUpperCase()}`}
            sx={{
              minHeight: 42,
              fontSize: '0.9375rem',
              borderStyle: 'dashed',
              color: 'text.secondary',
            }}
          />
        )}
      </Stack>
    </Box>
  )
}
